import { getTokenSilently } from 'packages/lib/auth';
import { loadConfig } from 'packages/lib/config';
import type { SWRResponse } from 'swr';
import type { ZodType, ZodTypeDef } from 'zod';
import type { SortOrder } from 'antd/lib/table/interface';
import {
  ResponseNotOk,
  UnexpectedResponseType,
} from 'packages/lib/utils/error';
const env = loadConfig();

export const getDefaultHeaders = async (): Promise<RequestInit> => ({
  headers: {
    'X-Requested-With': 'XMLHttpRequest',
    Accept: 'application/json',
    'Content-Type': 'application/json',
    Authorization: `Bearer ${(await getTokenSilently())?.accessToken}`,
  },
});

export type SWRFetcherError = UnexpectedResponseType | ResponseNotOk;
// TODO replace all with this
export type MLSWRResponse<T> = SWRResponse<T, SWRFetcherError>;
export type JSONResponse = Promise<[unknown, Response]>;
/**
 * @deprecated use /packages/lib/api/annotations/rest
 *
 * Caller must catch any thrown errors, unless throwOnError is set to false.
 * @param throwOnError -- If true (default), throws an Error for any unsuccessful response
 *
 * Example authXapi that does NOT use 'annotations' backend:
 *
   const env = loadConfig();
   const logMinerAPIHost = env.apiHosts.logminerHost;
   authXapi(
    new Request(`${logMinerAPIHost}${url}`, {
      method: 'PUT',
      ...(await getDefaultHeaders()),
      body: JSON.stringify(data),
    }),
    true, // throwOnError
    false // parse
  );
 */
export async function authXapi(reqInfo: RequestInfo): JSONResponse;
export async function authXapi(
  reqInfo: RequestInfo,
  throwOnError: boolean,
  parse: false
): Promise<Response>;
export async function authXapi(
  reqInfo: RequestInfo,
  throwOnError = true,
  parse = true
): Promise<unknown> {
  const req =
    typeof reqInfo === 'string'
      ? new Request(`${env.apiHosts.annotationsHost}${reqInfo}`, {
          ...(await getDefaultHeaders()),
        })
      : reqInfo;

  const resp = await window.fetch(req);

  if (throwOnError && !resp.ok) {
    throw new ResponseNotOk(resp);
  }

  if (parse) {
    return [await resp.json(), resp];
  }

  return resp;
}

export type APIBackend =
  | 'logMiner'
  | 'annotations'
  | 'trainingPlatformOld'
  | 'trainingPlatformEx'
  | 'drivelogs'
  | 'jobMetrics'
  | 'blobFrames' // https://confluence.ci.motional.com/confluence/display/BACK/Blob+Frames+API#BlobFramesAPI-GettingSupportedChannelandBlobTypeschannel-summary-api
  | 'techMetrics'
  | 'dtb'
  | 'airflow'
  | 'userMetrics'
  | 'taskHistory'
  | 'annotationsAppApi'
  | 'omnitag';

const BaseURLs = {
  logMiner: env.apiHosts.logminerHost,
  trainingPlatformOld: env.apiHosts.trainingPlatformHostOld,
  trainingPlatformEx: env.apiHosts.trainingPlatformHostEx,
  annotations: env.apiHosts.annotationsHost,
  drivelogs: env.apiHosts.drivelogsHost,
  jobMetrics: env.apiHosts.jobMetricsHost,
  blobFrames: env.apiHosts.blobFramesHost,
  techMetrics: env.apiHosts.techMetricsHost,
  dtb: env.apiHosts.dtbHost,
  airflow: env.apiHosts.airflowHost,
  userMetrics: env.apiHosts.userMetricsHost,
  taskHistory: env.apiHosts.taskHistoryHost,
  annotationsAppApi: env.apiHosts.annotationsAppApi,
  omnitag: env.apiHosts.omnitagHost,
};

export function getBaseURLForAPIBackend(
  backend: APIBackend | undefined
): string {
  if (!backend) return env.apiHosts.annotationsHost;
  return BaseURLs[backend] ?? env.apiHosts.annotationsHost;
}

export interface SWRFetcherOptions extends RequestInit {
  baseURL: APIBackend;
  excludeXRequestedWithHeader?: boolean;
  excludeContentTypeHeader?: boolean;
}
interface ZodSWRFetcherOptions<T, Z extends ZodTypeDef = ZodTypeDef>
  extends SWRFetcherOptions {
  zodParse: ZodType<T, Z>['parse'];
}
export interface LegacySWRFetcherOptions<T> extends SWRFetcherOptions {
  typeGuard: (arg: unknown) => arg is T;
}

interface LegacySWRFetcherOptionsWithResponse<T>
  extends LegacySWRFetcherOptions<T> {
  includeResponseObject: true;
}
interface SWRFetcherOptionsWithResponse<T, Z extends ZodTypeDef = ZodTypeDef>
  extends ZodSWRFetcherOptions<T, Z> {
  includeResponseObject: true;
}

export interface DataWithResponse<T> {
  data: T;
  resp: Response;
}

/**
 * @deprecated use /packages/lib/api/annotations/rest
 */
export async function swrFetcher<T, Z extends ZodTypeDef = ZodTypeDef>(
  url: string,
  options?: ZodSWRFetcherOptions<T, Z>
): Promise<T>;
export async function swrFetcher<T>(
  url: string,
  options?: LegacySWRFetcherOptions<T>
): Promise<T>;
export async function swrFetcher<T>(
  url: string,
  options?: LegacySWRFetcherOptionsWithResponse<T>
): Promise<DataWithResponse<T>>;
export async function swrFetcher<T, Z extends ZodTypeDef = ZodTypeDef>(
  url: string,
  options?: SWRFetcherOptionsWithResponse<T, Z>
): Promise<DataWithResponse<T>>;
export async function swrFetcher<T, Z extends ZodTypeDef = ZodTypeDef>(
  url: string,
  options?: LegacySWRFetcherOptions<T> | ZodSWRFetcherOptions<T, Z>
): Promise<unknown> {
  const {
    typeGuard = undefined,
    zodParse = undefined,
    baseURL = 'annotations',
    headers = undefined,
    includeResponseObject = false,
    ...fetchOptions
  } = {
    ...options,
  };

  const defaults = await getDefaultHeaders();
  if (defaults.headers) {
    if (options?.excludeXRequestedWithHeader)
      delete defaults.headers['X-Requested-With'];
    if (options?.excludeContentTypeHeader)
      delete defaults.headers['Content-Type'];
  }

  const res = await fetch(`${getBaseURLForAPIBackend(baseURL)}${url}`, {
    headers: {
      ...defaults.headers,
      ...headers,
    },
    ...fetchOptions,
  });

  if (!res.ok) {
    throw new ResponseNotOk(res);
  }

  const data = res.status === 204 ? null : await res.json();

  if (!typeGuard) {
    if (zodParse && data != null) {
      if (includeResponseObject) {
        return { data: zodParse(data), resp: res };
      }
      return zodParse(data);
    }
    return data;
  }

  if (includeResponseObject) {
    if (typeGuard(data)) {
      return { data, resp: res };
    }
  } else {
    if (typeGuard(data)) {
      return data;
    }
  }
  console.error(
    `Response for ${url} failed assertion with typeGuard in swrFetcher for response:`
  );
  console.log(data);

  throw new UnexpectedResponseType(res);
}

interface BlobFetcherOptions extends RequestInit {
  baseURL: 'logMiner' | 'annotations';
}

// Can be used to load blobs such as raw image, audio, pdf, etc. data
// Provides auth token to backend, just like authXapi()
/**
 * @deprecated use /packages/lib/api/annotations/rest
 */
export async function authBlobFetcher(
  url: string,
  options?: BlobFetcherOptions
): Promise<Blob> {
  const { baseURL, headers, ...fetchOptions } = {
    ...options,
  };

  const res = await fetch(`${getBaseURLForAPIBackend(baseURL)}${url}`, {
    headers: {
      ...(await getDefaultHeaders()).headers,
      ...headers,
    },
    ...fetchOptions,
  });

  if (!res.ok) {
    throw new Error('Error loading blob at url: ' + url);
  }

  return res.blob();
}

export interface SWRFetcherOptionsLoose<T> extends RequestInit {
  baseURL?: 'logMiner' | 'annotations';
  typeGuard?: (arg: unknown) => arg is T;
}
/**
 * @deprecated use /packages/lib/api/annotations/rest
 */
export async function swrFetcherLogminer<T>(
  url: string,
  options?: SWRFetcherOptionsLoose<T>
): Promise<T> {
  return swrFetcher<T>(url, {
    ...options,
    baseURL: options?.baseURL ?? 'logMiner',
  } as LegacySWRFetcherOptions<T>);
}

/**
 * Annotations api uses `-` to indictate sort direction
 */
export function toAnnotationsSortQP<T>({
  orderBy,
  orderDirection,
}: {
  orderBy?: keyof T;
  orderDirection?: SortOrder;
}): ['orderBy', string] | false {
  if (orderBy && orderDirection) {
    return [
      'orderBy',
      `${orderDirection === 'descend' ? '-' : ''}${String(orderBy)}`,
    ];
  }
  return false;
}
